import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = [
        "recurrentJobIdInput",
        "recurrentJobIdPreview",
        "dialog",
        "ctaCreateButton",
        "ctaAttachButton",
        "ctaInfo",
        "campaignNameContainer",
        "campaignNameInput",
    ];

    connect() {
        console.log("icm manual campaign creation controller connected");
    }

    previewRecurrentJobId() {
        const recurrentJobId = this.recurrentJobIdInputTarget.value;

        if (recurrentJobId.length !== 36) {
            this.recurrentJobIdPreviewTarget.classList.add("tw-hidden");
            return;
        }

        fetch(`/_/admin/indeed-campaign-management/sync-config/recurrent-job-info/${recurrentJobId}`)
            .then((response) => response.json())
            .then((data) => {
                if (data && data.occupationalFieldSearchterm) {
                    this.recurrentJobIdPreviewTarget.innerHTML = `
                        <div class="tw-font-semibold">${data.occupationalFieldSearchterm}</div>
                        <div class="tw-text-sm">${data.joboffererProfileBusinessName}</div>
                        <div class="tw-text-sm">${data.zipcode}</div>
                        <div class="tw-text-xs tw-text-gray-500">${recurrentJobId}</div>
                    `;
                    this.recurrentJobIdPreviewTarget.classList.remove("tw-hidden");

                    fetch(`/_/admin/indeed-campaign-management/campaign-type/${recurrentJobId}`)
                        .then((response) => response.json())
                        .then((typeData) => {
                            if (typeData && typeData.campaign_type) {
                                const typeDiv = document.createElement("div");
                                typeDiv.classList.add("tw-text-sm", "tw-font-bold", "tw-mt-2");
                                typeDiv.textContent = `Kampagnentyp: ${typeData.campaign_type}`;
                                this.recurrentJobIdPreviewTarget.appendChild(typeDiv);

                                this.updateCtas(typeData.campaign_type, recurrentJobId);
                            }
                        });

                    // Check for campaign conflicts
                    this.checkForCampaignConflicts(recurrentJobId);
                } else {
                    this.recurrentJobIdPreviewTarget.innerHTML = "Recurrent Job nicht gefunden.";
                    this.recurrentJobIdPreviewTarget.classList.remove("tw-hidden");
                }
            });
    }

    checkForCampaignConflicts(recurrentJobId) {
        fetch(`/_/admin/indeed-campaign-management/dashboard/check-conflicts/${recurrentJobId}`)
            .then((response) => response.json())
            .then((data) => {
                if (data.has_conflicts && data.conflicts.length > 0) {
                    this.showConflictWarning(data.conflicts);
                }
            })
            .catch((error) => {
                console.error("Error checking conflicts:", error);
            });
    }

    showConflictWarning(conflicts) {
        const conflictInfo = conflicts
            .map(
                (conflict) =>
                    `• ${conflict.name} (${conflict.campaign_type}) - Gültig: ${new Date(conflict.valid_from).toLocaleDateString("de-DE")} bis ${new Date(conflict.valid_until).toLocaleDateString("de-DE")}`,
            )
            .join("\n");

        const warningDiv = document.createElement("div");
        warningDiv.classList.add(
            "tw-mt-3",
            "tw-p-3",
            "tw-bg-yellow-50",
            "tw-border",
            "tw-border-yellow-200",
            "tw-rounded",
            "tw-text-sm",
        );
        const hasCustomer = Array.isArray(conflicts) && conflicts.some((c) => c.campaign_type === "customer");
        warningDiv.innerHTML = `
            <div class="tw-font-semibold tw-text-yellow-800 tw-mb-2">Hinweis zu bestehenden Kampagnen</div>
            <div class="tw-text-yellow-700 tw-mb-2">
                ${
                    hasCustomer
                        ? "Es existiert bereits mindestens eine aktive Kampagne. Für Kundenkampagnen können Sie die Anzeige einfach hinzufügen."
                        : "Diese Anzeige ist bereits Teil der folgenden aktiven Kampagnen:"
                }
            </div>
            <div class="tw-text-yellow-600 tw-whitespace-pre-line tw-mb-3">${conflictInfo}</div>
        `;

        // Remove any existing warning
        const existingWarning = this.recurrentJobIdPreviewTarget.querySelector(".tw-bg-yellow-50");
        if (existingWarning) {
            existingWarning.remove();
        }

        this.recurrentJobIdPreviewTarget.appendChild(warningDiv);
    }

    onDateChange() {
        // Re-trigger CTA update when dates change
        const recurrentJobId = this.hasRecurrentJobIdInputTarget ? this.recurrentJobIdInputTarget.value : "";
        if (recurrentJobId && recurrentJobId.length === 36) {
            // Re-fetch campaign type and update CTAs
            fetch(`/_/admin/indeed-campaign-management/campaign-type/${recurrentJobId}`)
                .then((response) => response.json())
                .then((typeData) => {
                    if (typeData && typeData.campaign_type) {
                        this.updateCtas(typeData.campaign_type, recurrentJobId);
                    }
                });
        }
    }

    updateCtas(campaignType, recurrentJobId) {
        const isCustomer = campaignType === "customer";

        const createBtn = this.hasCtaCreateButtonTarget ? this.ctaCreateButtonTarget : null;
        const attachBtn = this.hasCtaAttachButtonTarget ? this.ctaAttachButtonTarget : null;
        const infoBox = this.hasCtaInfoTarget ? this.ctaInfoTarget : null;

        // Reset
        [createBtn, attachBtn, infoBox].forEach((el) => el && el.classList.add("tw-hidden"));

        if (!isCustomer) {
            // SSTC/FLEX path → show create (manual single-job) form/button
            if (createBtn) createBtn.classList.remove("tw-hidden");
            return;
        }

        // Customer path → check for date overlaps with user-specified dates
        this.checkCustomerCampaignOverlaps(recurrentJobId, createBtn, attachBtn, infoBox);
    }

    checkCustomerCampaignOverlaps(recurrentJobId, createBtn, attachBtn, infoBox) {
        // Get the date inputs from the modal
        const dialog = this.dialogTarget;
        const startEl = dialog.querySelector("#start_date_modal");
        const endEl = dialog.querySelector("#end_date_modal");
        const startDate = startEl && startEl.value ? startEl.value : "";
        const endDate = endEl && endEl.value ? endEl.value : "";

        // Hide campaign name field initially
        if (this.hasCampaignNameContainerTarget) {
            this.campaignNameContainerTarget.classList.add("tw-hidden");
        }

        if (!startDate || !endDate) {
            // No dates specified yet, show create button and let user fill in dates
            if (createBtn) createBtn.classList.remove("tw-hidden");
            if (infoBox) {
                infoBox.textContent =
                    "Bitte wählen Sie Start- und Enddatum aus, um zu prüfen, ob eine bestehende Kundenkampagne vorhanden ist.";
                infoBox.classList.remove("tw-hidden");
            }
            return;
        }

        // Check for overlapping customer campaigns
        fetch(
            `/_/admin/indeed-campaign-management/manual-campaign-creation/check-customer-campaign-overlaps?recurrent_job_id=${encodeURIComponent(recurrentJobId)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
        )
            .then((response) => response.json())
            .then((data) => {
                if (data.error) {
                    console.error("Error checking overlaps:", data.error);
                    // Fallback: show create button
                    if (createBtn) createBtn.classList.remove("tw-hidden");
                    return;
                }

                const hasOverlaps = data.has_overlaps && data.overlaps && data.overlaps.length > 0;

                if (hasOverlaps) {
                    // Found overlapping campaigns → Offer attach to existing campaign
                    const overlappingCampaign = data.overlaps[0]; // Use the first overlapping campaign
                    if (attachBtn) attachBtn.classList.remove("tw-hidden");
                    if (infoBox) {
                        infoBox.textContent = `Eine bestehende Kundenkampagne mit überlappenden Daten gefunden: ${overlappingCampaign.name} (${new Date(overlappingCampaign.valid_from).toLocaleDateString("de-DE")} - ${new Date(overlappingCampaign.valid_until).toLocaleDateString("de-DE")}). Sie können die Anzeige zu dieser Kampagne hinzufügen.`;
                        infoBox.classList.remove("tw-hidden");
                    }
                    // Hide campaign name field for attach case
                    if (this.hasCampaignNameContainerTarget) {
                        this.campaignNameContainerTarget.classList.add("tw-hidden");
                    }
                } else {
                    // No overlapping campaigns → Offer create new campaign
                    if (createBtn) createBtn.classList.remove("tw-hidden");
                    if (infoBox) {
                        infoBox.textContent = `Keine bestehende Kundenkampagne mit überlappenden Daten gefunden. Es wird eine neue Kundenkampagne für den Zeitraum ${new Date(startDate).toLocaleDateString("de-DE")} - ${new Date(endDate).toLocaleDateString("de-DE")} erstellt.`;
                        infoBox.classList.remove("tw-hidden");
                    }
                    // Show campaign name field for create case and prefill it
                    this.showCampaignNameField(recurrentJobId);
                }
            })
            .catch((error) => {
                console.error("Error checking customer campaign overlaps:", error);
                // Fallback: show create button
                if (createBtn) createBtn.classList.remove("tw-hidden");
                if (infoBox) {
                    infoBox.textContent =
                        "Fehler beim Prüfen bestehender Kampagnen. Sie können trotzdem eine neue Kampagne erstellen.";
                    infoBox.classList.remove("tw-hidden");
                }
            });
    }

    showCampaignNameField(recurrentJobId) {
        if (!this.hasCampaignNameContainerTarget || !this.hasCampaignNameInputTarget) {
            return;
        }

        // Show the campaign name field
        this.campaignNameContainerTarget.classList.remove("tw-hidden");

        // Fetch customer info to prefill the name
        fetch(`/_/admin/indeed-campaign-management/sync-config/recurrent-job-info/${recurrentJobId}`)
            .then((response) => response.json())
            .then((data) => {
                if (data && data.customerInternalId) {
                    // Prefill with "Kunde <customerName>" format
                    this.campaignNameInputTarget.value = `Kunde ${data.customerInternalId}`;
                } else {
                    // Fallback if customer name not available
                    this.campaignNameInputTarget.value = "Kunde <Kundenname>";
                }
            })
            .catch((error) => {
                console.error("Error fetching customer info:", error);
                // Fallback
                this.campaignNameInputTarget.value = "Kunde <Kundenname>";
            });
    }

    submitAttach(event) {
        event.preventDefault();
        const recurrentJobId = this.hasRecurrentJobIdInputTarget ? this.recurrentJobIdInputTarget.value : "";
        if (!recurrentJobId || recurrentJobId.length !== 36) {
            return;
        }

        const form = document.createElement("form");
        form.method = "POST";
        form.action = "/_/admin/indeed-campaign-management/dashboard/attach-customer-job";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "recurrent_job_id";
        input.value = recurrentJobId;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
    }

    submitCreate(event) {
        event.preventDefault();
        const recurrentJobId = this.hasRecurrentJobIdInputTarget ? this.recurrentJobIdInputTarget.value : "";
        if (!recurrentJobId || recurrentJobId.length !== 36) {
            return;
        }

        // Read visible date inputs from the modal
        const dialog = this.dialogTarget;
        const startEl = dialog.querySelector("#start_date_modal");
        const endEl = dialog.querySelector("#end_date_modal");
        const start = startEl && startEl.value ? startEl.value : "";
        const end = endEl && endEl.value ? endEl.value : "";

        // Read campaign name if available
        const campaignName = this.hasCampaignNameInputTarget ? this.campaignNameInputTarget.value : "";

        const form = document.createElement("form");
        form.method = "POST";
        form.action = "/_/admin/indeed-campaign-management/manual-campaign-creation/create-campaign";

        const inputJob = document.createElement("input");
        inputJob.type = "hidden";
        inputJob.name = "recurrent_job_id";
        inputJob.value = recurrentJobId;
        form.appendChild(inputJob);

        const inputStart = document.createElement("input");
        inputStart.type = "hidden";
        inputStart.name = "start_date";
        inputStart.value = start;
        form.appendChild(inputStart);

        const inputEnd = document.createElement("input");
        inputEnd.type = "hidden";
        inputEnd.name = "end_date";
        inputEnd.value = end;
        form.appendChild(inputEnd);

        // Add campaign name if provided
        if (campaignName) {
            const inputCampaignName = document.createElement("input");
            inputCampaignName.type = "hidden";
            inputCampaignName.name = "campaign_name";
            inputCampaignName.value = campaignName;
            form.appendChild(inputCampaignName);
        }

        document.body.appendChild(form);
        form.submit();
    }

    checkForCampaignConflicts(recurrentJobId) {
        fetch(`/_/admin/indeed-campaign-management/dashboard/check-conflicts/${recurrentJobId}`)
            .then((response) => response.json())
            .then((data) => {
                if (data.has_conflicts && data.conflicts.length > 0) {
                    this.showConflictWarning(data.conflicts);
                }
            })
            .catch((error) => {
                console.error("Error checking conflicts:", error);
            });
    }

    showConflictWarning(conflicts) {
        const conflictInfo = conflicts
            .map(
                (conflict) =>
                    `• ${conflict.name} (${conflict.campaign_type}) - Gültig: ${new Date(conflict.valid_from).toLocaleDateString("de-DE")} bis ${new Date(conflict.valid_until).toLocaleDateString("de-DE")}`,
            )
            .join("\n");

        const warningDiv = document.createElement("div");
        warningDiv.classList.add(
            "tw-mt-3",
            "tw-p-3",
            "tw-bg-yellow-50",
            "tw-border",
            "tw-border-yellow-200",
            "tw-rounded",
            "tw-text-sm",
        );
        const hasCustomer = Array.isArray(conflicts) && conflicts.some((c) => c.campaign_type === "customer");
        warningDiv.innerHTML = `
            <div class="tw-font-semibold tw-text-yellow-800 tw-mb-2">Hinweis zu bestehenden Kampagnen</div>
            <div class="tw-text-yellow-700 tw-mb-2">
                ${
                    hasCustomer
                        ? "Es existiert bereits mindestens eine aktive Kampagne. Für Kundenkampagnen können Sie die Anzeige einfach hinzufügen."
                        : "Diese Anzeige ist bereits Teil der folgenden aktiven Kampagnen:"
                }
            </div>
            <div class="tw-text-yellow-600 tw-whitespace-pre-line tw-mb-3">${conflictInfo}</div>
        `;

        // Remove any existing warning
        const existingWarning = this.recurrentJobIdPreviewTarget.querySelector(".tw-bg-yellow-50");
        if (existingWarning) {
            existingWarning.remove();
        }

        this.recurrentJobIdPreviewTarget.appendChild(warningDiv);
    }

    openManualCampaignCreationDialog() {
        this.dialogTarget.showModal();
        // Reset state when opening dialog
        this.recurrentJobIdInputTarget.value = "";
        if (this.hasCtaInfoTarget) {
            this.ctaInfoTarget.classList.add("tw-hidden");
        }
        if (this.hasCtaCreateButtonTarget) {
            this.ctaCreateButtonTarget.classList.add("tw-hidden");
        }
        if (this.hasCtaAttachButtonTarget) {
            this.ctaAttachButtonTarget.classList.add("tw-hidden");
        }
        if (this.hasCampaignNameContainerTarget) {
            this.campaignNameContainerTarget.classList.add("tw-hidden");
        }
    }

    closeManualCampaignCreationDialog() {
        this.dialogTarget.close();
    }
}
